import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { radiusForPreset, type StoreRadiusPreset } from '@fewerbags/core';
import type { StoreDto } from '@fewerbags/schema';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Field } from '../Field';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { RadiusField } from './RadiusField';

/** Add-store form: chain/name/address plus lat/lng (required by the API), with a
 *  "use my location" shortcut since typing coordinates by hand is unpleasant,
 *  and the detection radius (Normal 150 m / Big-box 300 m / custom). */
export function AddStoreForm({ onAdded }: { onAdded: (store: StoreDto) => void }) {
  const [chain, setChain] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [radiusPreset, setRadiusPreset] = useState<StoreRadiusPreset>('normal');
  const [radiusCustom, setRadiusCustom] = useState('');
  const [radiusError, setRadiusError] = useState<string | undefined>(undefined);
  const [formError, setFormError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  const qc = useQueryClient();

  const create = useMutation({
    mutationFn: (input: Parameters<typeof api.stores.create>[0]) => api.stores.create(input),
    onSuccess: (store) => {
      setChain('');
      setName('');
      setAddress('');
      setLat('');
      setLng('');
      setRadiusPreset('normal');
      setRadiusCustom('');
      void qc.invalidateQueries({ queryKey: ['stores', 'list'] });
      onAdded(store);
    },
  });

  function useMyLocation() {
    if (!('geolocation' in navigator)) {
      setFormError('This device does not support location lookup — enter coordinates by hand.');
      return;
    }
    setLocating(true);
    setFormError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(String(pos.coords.latitude));
        setLng(String(pos.coords.longitude));
        setLocating(false);
      },
      () => {
        setFormError('Could not get your location. Enter coordinates by hand.');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    setRadiusError(undefined);
    const latNum = Number(lat);
    const lngNum = Number(lng);
    if (!chain.trim() || !name.trim()) {
      setFormError('Enter a chain and a name.');
      return;
    }
    if (!Number.isFinite(latNum) || latNum < -90 || latNum > 90) {
      setFormError('Enter a valid latitude, or use "Use my location".');
      return;
    }
    if (!Number.isFinite(lngNum) || lngNum < -180 || lngNum > 180) {
      setFormError('Enter a valid longitude, or use "Use my location".');
      return;
    }
    const radiusM = radiusForPreset(radiusPreset, radiusCustom);
    if (radiusM === null) {
      setRadiusError('Enter a whole number of metres in range.');
      return;
    }
    create.mutate({
      chain: chain.trim(),
      name: name.trim(),
      address: address.trim() ? address.trim() : null,
      lat: latNum,
      lng: lngNum,
      radiusM,
    });
  }

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-3">
      <Field
        id="store-add-chain"
        label="Chain"
        placeholder="Publix, Kroger…"
        value={chain}
        onChange={(e) => setChain(e.target.value)}
        required
      />
      <Field
        id="store-add-name"
        label="Store name"
        placeholder="Publix at Five Points"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />
      <Field
        id="store-add-address"
        label="Address (optional)"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
      />
      <div className="grid grid-cols-2 gap-3">
        <Field
          id="store-add-lat"
          label="Latitude"
          type="number"
          inputMode="decimal"
          step="any"
          value={lat}
          onChange={(e) => setLat(e.target.value)}
          required
        />
        <Field
          id="store-add-lng"
          label="Longitude"
          type="number"
          inputMode="decimal"
          step="any"
          value={lng}
          onChange={(e) => setLng(e.target.value)}
          required
        />
      </div>
      <Button type="button" variant="secondary" onClick={useMyLocation} disabled={locating}>
        {locating ? 'Locating…' : 'Use my location'}
      </Button>
      <RadiusField
        idPrefix="store-add"
        preset={radiusPreset}
        custom={radiusCustom}
        onPresetChange={setRadiusPreset}
        onCustomChange={setRadiusCustom}
        error={radiusError}
      />
      {formError ? <Alert tone="error">{formError}</Alert> : null}
      {create.isError ? <Alert tone="error">{messageForError(create.error)}</Alert> : null}
      <Button type="submit" disabled={create.isPending}>
        {create.isPending ? 'Adding…' : 'Add store'}
      </Button>
    </form>
  );
}
